import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const GOLD = '#C9A84C';
const BG = '#050810';
const SURFACE = '#0B1120';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';
const RED = '#FF4444';
const GREEN = '#22C55E';

const s = {
  page: {
    background: BG,
    color: '#fff',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    minHeight: '100vh',
  },
  wrap: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '72px 24px 80px',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(201,168,76,0.12)',
    border: `1px solid rgba(201,168,76,0.3)`,
    color: GOLD,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '6px 14px',
    borderRadius: 20,
    marginBottom: 24,
  },
  h1: {
    fontSize: 'clamp(28px,5vw,42px)',
    fontWeight: 800,
    margin: '0 0 16px',
    lineHeight: 1.15,
  },
  sub: {
    color: OFF_WHITE,
    fontSize: 17,
    lineHeight: 1.7,
    margin: '0 0 56px',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: OFF_WHITE,
    marginBottom: 20,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 48,
  },
  thMane: {
    background: 'rgba(201,168,76,0.1)',
    color: GOLD,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: `1px solid rgba(201,168,76,0.2)`,
    borderBottom: `2px solid ${GOLD}`,
  },
  thBooksy: {
    background: SURFACE,
    color: OFF_WHITE,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: `1px solid ${BORDER}`,
  },
  thLabel: {
    background: 'transparent',
    color: OFF_WHITE,
    fontWeight: 600,
    fontSize: 12,
    padding: '14px 0',
    textAlign: 'left',
    letterSpacing: '0.04em',
    border: 'none',
    borderBottom: `1px solid ${BORDER}`,
  },
  tdLabel: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 0',
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 500,
    verticalAlign: 'top',
  },
  tdMane: {
    color: '#fff',
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: `1px solid rgba(201,168,76,0.15)`,
    background: 'rgba(201,168,76,0.04)',
    border: `1px solid rgba(201,168,76,0.1)`,
    verticalAlign: 'top',
  },
  tdBooksy: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: `1px solid ${BORDER}`,
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    verticalAlign: 'top',
  },
  callout: {
    background: 'rgba(201,168,76,0.07)',
    border: `1px solid rgba(201,168,76,0.25)`,
    borderRadius: 12,
    padding: '28px 32px',
    marginBottom: 48,
  },
  calloutLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: GOLD,
    marginBottom: 12,
  },
  calloutText: {
    color: '#fff',
    fontSize: 16,
    lineHeight: 1.8,
    margin: 0,
  },
  faqItem: {
    borderBottom: `1px solid ${BORDER}`,
    paddingBottom: 28,
    marginBottom: 28,
  },
  faqQ: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 10,
  },
  faqA: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.7,
    margin: 0,
  },
  cta: {
    background: 'rgba(201,168,76,0.07)',
    border: `1px solid rgba(201,168,76,0.25)`,
    borderRadius: 16,
    padding: '48px 40px',
    textAlign: 'center',
    marginTop: 56,
  },
  ctaTitle: {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 12,
  },
  ctaSub: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: 32,
  },
  btn: {
    display: 'inline-block',
    background: GOLD,
    color: '#000',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '16px 40px',
    borderRadius: 8,
    textDecoration: 'none',
  },
};

const rows = [
  {
    label: 'Monthly Base Price',
    mane: '$29/mo — locked forever',
    booksy: '$29.99/mo',
    maneGood: true,
  },
  {
    label: 'Commission on New Clients',
    mane: <span style={{ color: GREEN }}>0% — always</span>,
    booksy: <span style={{ color: RED }}>30% of first visit via Boost<br /><span style={{ fontSize: 12, color: OFF_WHITE }}>(min $10, max $100 per client)</span></span>,
    maneGood: true,
  },
  {
    label: 'Client App Download Required',
    mane: <span style={{ color: GREEN }}>No — books from any browser</span>,
    booksy: <span style={{ color: RED }}>Yes — Booksy app required</span>,
    maneGood: true,
  },
  {
    label: 'Client Booking Fee',
    mane: <span style={{ color: GREEN }}>$0</span>,
    booksy: 'Varies by plan',
    maneGood: true,
  },
  {
    label: 'Client Data Ownership',
    mane: <span style={{ color: GREEN }}>Yours. Export anytime.</span>,
    booksy: <span style={{ color: RED }}>Cannot be exported. Stays on Booksy.</span>,
    maneGood: true,
  },
  {
    label: 'SMS Reminders',
    mane: <span style={{ color: GREEN }}>Included</span>,
    booksy: 'Add-on cost',
    maneGood: true,
  },
  {
    label: 'AI Re-Engagement',
    mane: <span style={{ color: GREEN }}>Included</span>,
    booksy: <span style={{ color: RED }}>Not available</span>,
    maneGood: true,
  },
  {
    label: 'Client Discovery Marketplace',
    mane: 'No (bring your own clients)',
    booksy: 'Yes (via Boost, at 30% commission)',
    maneGood: false,
  },
  {
    label: 'Contract',
    mane: 'Month-to-month',
    booksy: 'Month-to-month',
    maneGood: false,
  },
];

const faqs = [
  {
    q: 'Is Boost required on Booksy?',
    a: "No. But without it, Booksy doesn't send you new clients. It's the feature that makes Booksy useful for growth — and it's the feature that charges 30% commission. Without Boost, you're paying $30/mo for a calendar app.",
  },
  {
    q: 'Can I export my client list from Booksy?',
    a: "No. Your client data lives in Booksy's system. If you cancel, you don't leave with your clients' contact information. With Mane, your data is yours. Export it anytime in a format you can actually use.",
  },
  {
    q: "Do my clients have to download an app to book through Mane?",
    a: "No. Your booking link opens in any browser. Clients pick a time, confirm, done. No app download, no account creation, no friction. Every extra step loses a percentage of clients — we removed them.",
  },
  {
    q: "What happens to my price if Mane raises rates?",
    a: "Founding members are locked at $29/mo for life. That's in your membership agreement. When we raise prices for new members, your rate doesn't move. Ever.",
  },
  {
    q: "What does Mane not have that Booksy does?",
    a: "Booksy has a marketplace — clients can discover you by searching Booksy's directory. Mane doesn't. We're a tool for professionals who already have a clientele and want to keep more of what they earn. If you have zero clients and need a discovery platform, Booksy's marketplace has value. If you're tired of paying a cut of your own clients, Mane is built for you.",
  },
];

export default function VsBooksy() {
  return (
    <div style={s.page}>
      <Helmet>
        <title>Mane vs. Booksy — The Full Comparison</title>
        <meta name="description" content="Booksy charges $29.99/mo to start. Then the fees begin. Here's what the comparison actually looks like — including the Boost commission most barbers never add up." />
        <meta property="og:title" content="Mane vs. Booksy — The Full Comparison" />
        <meta property="og:description" content="30% commission on every new client via Boost. Client data you can't export. App download required. Here's the real math." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <div style={s.wrap}>
        <div style={s.badge}>Mane vs. Booksy</div>
        <h1 style={s.h1}>The comparison Booksy's ad doesn't show you.</h1>
        <p style={s.sub}>
          Booksy's base price is $29.99/mo. That's real. So is everything that comes after it.
          This page exists because you deserve the full math before you commit.
        </p>

        <div style={s.sectionTitle}>Side-by-side breakdown</div>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.thLabel}></th>
              <th style={s.thMane}>Mane (Founding)</th>
              <th style={s.thBooksy}>Booksy</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={s.tdLabel}>{r.label}</td>
                <td style={s.tdMane}>{r.mane}</td>
                <td style={s.tdBooksy}>{r.booksy}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={s.callout}>
          <div style={s.calloutLabel}>Real math</div>
          <p style={s.calloutText}>
            Barber with 10 new Boost clients/month. Average first visit: $80.<br />
            Booksy Boost fee: 30% × $800 = <strong style={{ color: RED }}>$240/mo in Boost fees alone.</strong><br />
            Plus $29.99 base = <strong style={{ color: RED }}>$270/mo total.</strong><br /><br />
            Mane founding rate: <strong style={{ color: GREEN }}>$29/mo. Full stop.</strong>
          </p>
        </div>

        <div style={s.sectionTitle}>Common questions</div>
        {faqs.map((f, i) => (
          <div key={i} style={s.faqItem}>
            <div style={s.faqQ}>{f.q}</div>
            <p style={s.faqA}>{f.a}</p>
          </div>
        ))}

        <div style={s.cta}>
          <div style={s.ctaTitle}>Ready to make the switch?</div>
          <p style={s.ctaSub}>
            50 founding spots. Pay $199 once, then $29/mo locked for life.<br />
            When the spots fill, the price goes to $59/mo — no exceptions.
          </p>
          <Link to="/founding" style={s.btn}>Claim Your Founding Spot</Link>
          <p style={{ color: OFF_WHITE, fontSize: 13, marginTop: 20 }}>
            Questions? <a href="mailto:christiangflairbiz@gmail.com" style={{ color: GOLD }}>Email Christian directly.</a> He answers.
          </p>
        </div>
      </div>
    </div>
  );
}
